@file:NoLiveLiterals

package com.example.borshi.web.home

import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.model.Category
import com.example.borshi.web.model.NutritionistBasePlan
import com.example.borshi.web.model.NutritionistProfile
import com.example.borshi.web.model.Product

// ─────────────────────────────────────────────────────────────────────────────
// View-Mode Enum — replaces magic strings "adhoc" / "subscription"
// ─────────────────────────────────────────────────────────────────────────────
enum class ViewMode { ADHOC, SUBSCRIPTION }

// ─────────────────────────────────────────────────────────────────────────────
// Urgency Timer Value Object
// ─────────────────────────────────────────────────────────────────────────────
data class UrgencyTimerState(
    val secondsRemaining: Long = 0L
) {
    val isUrgent: Boolean get() = secondsRemaining < AppConfig.URGENCY_THRESHOLD_SECONDS

    val formattedTime: String
        get() {
            val h = secondsRemaining / AppConfig.SECONDS_PER_HOUR
            val m = (secondsRemaining % AppConfig.SECONDS_PER_HOUR) / AppConfig.SECONDS_PER_MINUTE
            val s = secondsRemaining % AppConfig.SECONDS_PER_MINUTE
            return "${h.toString().padStart(2, '0')}h " +
                   "${m.toString().padStart(2, '0')}m " +
                   "${s.toString().padStart(2, '0')}s"
        }

    fun tick(): UrgencyTimerState = copy(secondsRemaining = (secondsRemaining - 1).coerceAtLeast(0L))
}

// ─────────────────────────────────────────────────────────────────────────────
// Immutable Screen State — single source of truth for the Home composable
// ─────────────────────────────────────────────────────────────────────────────
data class HomeScreenState(
    // ── Ad-hoc data ───────────────────────────────────────────────────────
    val categories:       List<Category>             = emptyList(),
    val featuredProducts: List<Product>              = emptyList(),

    // ── Subscription data ─────────────────────────────────────────────────
    val nutritionists:    List<NutritionistProfile>  = emptyList(),
    val plans:            List<NutritionistBasePlan> = emptyList(),

    // ── UI state ──────────────────────────────────────────────────────────
    val viewMode:         ViewMode                   = ViewMode.ADHOC,
    val isLoading:        Boolean                    = true,
    val isSubLoading:     Boolean                    = false,
    val error:            AppException?              = null,
    val urgencyTimer:     UrgencyTimerState          = UrgencyTimerState(),
    val feedbackImages:   List<String>               = emptyList()
) {
    /** True only while the ad-hoc data is being initially loaded */
    val showAdhocSkeleton: Boolean get() = isLoading && categories.isEmpty()

    /** True only while subscription data is being loaded */
    val showSubSkeleton:   Boolean get() = isSubLoading && nutritionists.isEmpty()
}

// ─────────────────────────────────────────────────────────────────────────────
// Sealed Actions — exhaustive intent API; eliminates ad-hoc setState calls
// ─────────────────────────────────────────────────────────────────────────────
sealed class HomeScreenAction {
    object LoadAdhocData        : HomeScreenAction()
    object LoadSubscriptionData : HomeScreenAction()
    object LoadFeedbackImages   : HomeScreenAction()
    data class ChangeViewMode(val mode: ViewMode) : HomeScreenAction()
    object TickTimer            : HomeScreenAction()
    object ClearError           : HomeScreenAction()
}

